import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { createCartWriteQueue } from '@theme/cart-write-queue';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  SubscriptionToggleUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { amplitudeTrackClick } from "@theme/amplitude-custom-events";


/**
 * Full DOM replacement for the cart section.
 * Swaps innerHTML instead of diffing to avoid visual glitches from partial morph.
 * Handles Alpine lifecycle and dispatches the same event as morphSection.
 * @param {string} sectionId
 * @param {string} html
 */
function replaceSection(sectionId, html) {
  const sectionSelector = `shopify-section-${sectionId}`;
  const existingEl = document.getElementById(sectionSelector);
  if (!existingEl) return;

  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const newEl = fragment.getElementById(sectionSelector);
  if (!newEl) return;

  if (window.Alpine) window.Alpine.destroyTree(existingEl);

  existingEl.innerHTML = newEl.innerHTML;

  if (window.Alpine) window.Alpine.initTree(existingEl);

  queueMicrotask(() => {
    existingEl.dispatchEvent(new CustomEvent('theme:section:morphed', {
      bubbles: true,
      detail: { morphedElement: existingEl, sectionId, replaced: true }
    }));
  });
}

window.replaceCartSection = replaceSection;

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  // Serialized, coalescing cart-write queue. Every cart mutation flows through
  // this so only one /cart/change is in flight at a time and a burst of clicks
  // collapses to a leading request plus, at most, one trailing reconciliation.
  // The section is re-rendered once, on drain — never on intermediate completions.
  #queue = createCartWriteQueue({
    send: (line, payload) => this.#sendCartWrite(line, /** @type {{ quantity: number, sellingPlanId: number|null }} */ (payload)),
    onDrain: (response) => this.#finalizeCartWrite(response),
    onError: (line, response, error) => this.#handleCartWriteError(line, response, error),
    // A successful /cart/change response is always the full cart (has an `items`
    // array). Any error shape — {errors}, {status, message, description}, etc. —
    // lacks it, so this flags every failure mode, not just `.errors`.
    isError: (response) => !Array.isArray(/** @type {{ items?: unknown }} */ (response)?.items),
  });
  // Perf marker for the current burst (start on first enqueue, measure on settle).
  #burstPerfMarker = null;
  #boundSignInCheckoutHandler = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);
    document.addEventListener(ThemeEvents.subscriptionToggleUpdate, this.#onSubscriptionToggleUpdate);

    this.checkoutButtons = this.querySelectorAll('[name="checkout"], [data-sign-in-checkout]');
    this.shareCartButtons = this.querySelectorAll('[data-share-cart-button]');

    // NOTE: Checkout button click is now handled by Alpine checkoutInterceptor
    // which calls this.handleCheckoutClick() after points reservation logic

    this.shareCartButtons.forEach(button => {
      button.addEventListener('click', this.#handleShareCartClick.bind(this));
    });

    // Handle sign-in checkout button clicks (prepares cart before redirect to login)
    this.#boundSignInCheckoutHandler = this.#handleSignInCheckoutClick.bind(this);
    document.addEventListener('click', this.#boundSignInCheckoutHandler);

    this.calculateTotalLoyaltyPoints();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);
    document.removeEventListener(ThemeEvents.subscriptionToggleUpdate, this.#onSubscriptionToggleUpdate);
    document.removeEventListener('click', this.#boundSignInCheckoutHandler);
  }

  /**
   * Handles the subscription toggle update event. Syncs cart state first, then
   * routes the change through the shared cart-write queue so a toggle can never
   * race a concurrent quantity write. Bound as a field for add/removeEventListener.
   * @param {SubscriptionToggleUpdateEvent} event - The event.
   */
  #onSubscriptionToggleUpdate = async (event) => {
    if (typeof window.syncCartState === 'function') {
      await window.syncCartState();
    }
    const { resource, sourceId, itemQuantity } = event.detail;
    this.enqueueCartWrite(Number(resource), {
      quantity: itemQuantity,
      sellingPlanId: sourceId ? Number(sourceId) : null,
      action: 'change',
    });
  };

  #handleShareCartClick(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    const { target } = event;
  }

  /**
   * Handles clicks on sign-in checkout buttons.
   * Delegates to handleCheckoutClick with the login redirect URL.
   * @param {Event} event - The click event
   */
  #handleSignInCheckoutClick(event) {
    const target = event.target.closest('[data-sign-in-checkout]');
    if (!target) return;

    const redirectUrl = target.getAttribute('data-redirect-url') || target.getAttribute('href') || '/account/login';
    this.handleCheckoutClick(event, redirectUrl);
  }

  /**
   * Public method called by Alpine checkoutInterceptor after points reservation.
   * Handles PV/CV/LP updates and adds free gifts to cart before checkout.
   * @param {Event} event - The click event
   * @param {string} [redirectUrl] - Optional custom redirect URL (e.g., login page)
   * @param {ShopifyCart} [cartSnapshot] - Cart the promotions checkout commit was
   *   evaluated against and wrote to. Supplied by the checkout interceptor so this
   *   step doesn't re-read a cart nothing has changed since. The sign-in path skips
   *   promotions entirely and passes nothing, so it still reads the cart here.
   */
  async handleCheckoutClick(event, redirectUrl, cartSnapshot) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    // Loading/disabled state now managed by Alpine's isProcessing binding
    var membershipProductId = document.querySelector('[name="properties[_membership_product]"]')?.value;

    // Determine final redirect URL
    const locale = `${window.Shopify?.routes?.root || '/'}checkout`;
    const finalRedirectUrl = redirectUrl || event?.target?.getAttribute('href') || locale;

    try {
      // TODO: PV/CV/LP updates temporarily disabled for checkout performance investigation

      // Check for a membership product. Promotions never touch membership lines
      // (its removals are _promo_handle lines, its additions are gift lines), so
      // the snapshot the checkout commit already evaluated answers this. Without
      // one — the sign-in path — read the cart.
      let cart = cartSnapshot;
      if (!cart) {
        const cartResp = await fetch(`${Theme.routes.cart_url}.js`);
        if (!cartResp.ok) {
          throw new Error(`cart-items.handleCheckoutClick: ${Theme.routes.cart_url}.js failed: status ${cartResp.status}`);
        }
        cart = await cartResp.json();
      }
      const foundMembershipProduct = cart.items.find(item => item?.properties?._membership_product === "true");

      // Add membership product for guests
      if (!window.customer && !foundMembershipProduct && !redirectUrl && membershipProductId) {
        const addResp = await fetch(`${Theme.routes.cart_add_url}`, fetchConfig('json', {
          body: JSON.stringify({
            items: [{
              id: membershipProductId,
              quantity: 1,
              properties: {
                _membership_product: "true",
              },
            }],
          })
        }));
        if (!addResp.ok) {
          throw new Error(`cart-items.handleCheckoutClick: ${Theme.routes.cart_add_url} failed: status ${addResp.status}`);
        }
      }

      // If membership is present and customer is logged in, remove membership product
      if (foundMembershipProduct && window.customer) {
        const changeResp = await fetch(`${Theme.routes.cart_change_url}`,
          fetchConfig('json', {
            body: JSON.stringify({ id: foundMembershipProduct.key, quantity: 0 })
          })
        );
        if (!changeResp.ok) {
          throw new Error(`cart-items.handleCheckoutClick: ${Theme.routes.cart_change_url} failed: status ${changeResp.status}`);
        }
      }

      amplitudeTrackClick({
        CLICK_NAME: "CHECKOUT_START",
        CLICK_LOCATION: "CART_PAGE",
      })

      // Auth redirect: use replace() so the pre-auth cart entry is removed from
      // history. After login, return_to creates a fresh /cart entry that becomes
      // the proper back-button target from checkout.
      if (redirectUrl) {
        window.location.replace(finalRedirectUrl);
      } else {
        window.location.href = finalRedirectUrl;
      }
    } catch (error) {
      // Fail loudly: keep the user on /cart, show generic toast, re-enable the button.
      // The previous behavior (force-redirect to /checkout) silently sent users into
      // checkout with a half-prepared cart.
      console.error('Error in checkout process:', error);
      const errorMessage = window.cartCheckoutTranslations?.errorGeneric
        ?? 'Oops, we ran into a problem. Please try again and if the issue persists call Customer service';
      window.Alpine?.store('toastStore')?.error(errorMessage);
      if (typeof window.resetCheckoutState === 'function') {
        window.resetCheckoutState();
      }
    }
  }

  /**
   * Handles QuantitySelectorUpdateEvent — routes the change through the cart-write
   * queue. Bound as a field so add/removeEventListener share one reference.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #handleQuantitySelectorUpdate = (event) => {
    const { quantity, cartLine: line, sellingPlanId } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.enqueueCartWrite(line, {
      quantity,
      sellingPlanId: sellingPlanId ? Number(sellingPlanId) : null,
      action: 'change',
    });

    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  };

  /**
   * Enqueues a single cart write through the coalescing queue. Opens a burst
   * (perf marker, disable lock, total shimmer) the first time the queue is idle;
   * subsequent writes during the burst coalesce and reuse the same lock/shimmer.
   * @param {number} line - The cart line index.
   * @param {{ quantity: number, sellingPlanId: number|null, action: string }} payload
   */
  enqueueCartWrite(line, payload) {
    const numericLine = Number(line);
    if (!numericLine) return;

    if (!this.#queue.isBusy()) {
      this.#burstPerfMarker = cartPerformance.createStartingMarker(`${payload.action || 'change'}:user-action`);
      this.#disableCartItems();
      this.refs.cartTotal?.shimmer();
    }

    this.#queue.enqueue(numericLine, payload);
  }

  /**
   * Resolves once every queued cart write has landed and the section re-render
   * has run. Awaited by the Alpine checkout interceptor before it evaluates
   * promotions, so a quantity change still in flight isn't raced past.
   * @returns {Promise<void>}
   */
  whenCartWritesIdle() {
    return this.#queue.whenIdle();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   * @param {Event} [event] - The click event (optional).
   */
  onLineItemRemove(line, event) {
    // Add loading state to the remove button
    const removeButton = event?.target?.closest?.('.cart-items__remove');
    if (removeButton) {
      removeButton.classList.add('is-loading');
    }

    // Look up the item's selling plan so subscription-only products
    // keep their plan in the request (avoids Shopify rejecting null).
    const cartItem = Theme.globalVariables.cart?.items?.[line - 1];
    const sellingPlanId = cartItem?.selling_plan_allocation?.selling_plan?.id ?? null;

    this.updateQuantity({
      line,
      quantity: 0,
      sellingPlanId,
      action: 'clear',
    });

    // `line` is 1-based (matches data-line-key); cartItemRows is a 0-based array.
    // Use line - 1 so the removal animation targets the row the user clicked
    // rather than the next one down.
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const remove = () => cartItemRowToRemove.remove();

    if (prefersReducedMotion()) return remove();

    // Add class to the row to trigger the animation
    cartItemRowToRemove.style.setProperty('--row-height', `${cartItemRowToRemove.clientHeight}px`);
    cartItemRowToRemove.classList.add('removing');

    // Remove the row after the animation ends
    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /**
   * Updates the quantity. Compatibility shim — all cart writes now flow through
   * the coalescing queue via {@link enqueueCartWrite}.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {number|null} config.sellingPlanId - The selling plan id.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    this.enqueueCartWrite(config.line, {
      quantity: config.quantity,
      sellingPlanId: config.sellingPlanId,
      action: config.action,
    });
  }

  /**
   * Performs one cart write (the queue's `send`). Builds the request from the
   * freshest cart snapshot, posts to /cart/change, caches a valid cart response,
   * and returns the parsed body. Does NOT render — that happens once, on drain.
   * @param {number} line - The cart line index (1-based, matches data-line-key).
   * @param {{ quantity: number, sellingPlanId: number|null }} payload
   * @returns {Promise<any>} the parsed /cart/change response
   */
  async #sendCartWrite(line, payload) {
    const { quantity, sellingPlanId } = payload;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    // Resolve the line item key from the freshest cart snapshot, by line position.
    // Shopify rotates a line's `key` on every mutation, so the DOM's data-item-key
    // goes stale the moment the previous write in this burst succeeds. The section
    // is not re-rendered until the queue drains, so the DOM key cannot be trusted
    // mid-burst. Theme.globalVariables.cart is refreshed after each successful send
    // and carries the rotated key; line position is stable within a burst (quantity
    // changes never reorder). Fall back to the DOM key only when no cached cart
    // line exists (e.g. first paint before any write).
    const cartItem = Theme.globalVariables.cart?.items?.[line - 1];
    const row = this.querySelector(`[data-line-key="${line}"]`);
    const itemKey = cartItem?.key ?? row?.dataset.itemKey;

    const currentSellingPlan = cartItem?.selling_plan_allocation?.selling_plan?.id ?? null;
    const sellingPlanChanged = sellingPlanId !== currentSellingPlan;
    let properties;
    if (sellingPlanChanged && cartItem?.properties) {
      properties = { ...cartItem.properties, _loyalty_type: sellingPlanId ? 'loyalty' : 'one-time' };
    } else if (sellingPlanChanged) {
      properties = { _loyalty_type: sellingPlanId ? 'loyalty' : 'one-time' };
    }

    const body = JSON.stringify({
      id: itemKey,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
      selling_plan: sellingPlanId,
      ...(properties ? { properties } : {}),
    });

    const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
    const parsed = JSON.parse(await response.text());

    // Only cache a valid cart. An error body (e.g. {errors|message|status} with no
    // items array) must never overwrite the cached cart — doing so wedges every
    // subsequent write. The queue's `isError` predicate flags the same shape so the
    // onError path reconciles instead of rendering.
    if (Array.isArray(parsed?.items)) {
      Theme.globalVariables.cart = parsed;
    }
    return parsed;
  }

  /**
   * Renders the cart once the queue drains (the queue's `onDrain`). Runs from the
   * most recent successful response so a burst re-renders the section only once.
   * @param {any} parsedResponseText - The newest successful /cart/change response.
   */
  async #finalizeCartWrite(parsedResponseText) {
    try {
      resetShimmer(this);

      let sectionHtml = parsedResponseText?.sections?.[this.sectionId];
      if (!sectionHtml) {
        if (typeof window.refreshCart === 'function') {
          await window.refreshCart();
          this.calculateTotalLoyaltyPoints();
          return;
        }
        sectionHtml = await sectionRenderer.getSectionHTML(this.sectionId, false);
      }

      const newSectionHTML = new DOMParser().parseFromString(sectionHtml, 'text/html');

      // Grab the new cart item count from a hidden element
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.updateCartItemCount(newCartItemCount);

      this.dispatchEvent(
        new CartUpdateEvent(parsedResponseText, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponseText.sections,
        })
      );

      replaceSection(this.sectionId, sectionHtml);
      this.calculateTotalLoyaltyPoints();
    } catch (error) {
      console.error(error);
    } finally {
      this.#endBurst();
    }
  }

  /**
   * Handles a failed cart write (the queue's `onError`). The queue has already
   * dropped any pending writes for this burst; reconcile the UI to server truth.
   * @param {number} line - The line whose write failed.
   * @param {any} [parsedResponseText] - The error response, if the request resolved.
   * @param {unknown} [error] - The thrown error, if the request rejected.
   */
  #handleCartWriteError(line, parsedResponseText, error) {
    if (error) console.error(error);
    resetShimmer(this);

    try {
      if (parsedResponseText?.errors) {
        // Inline line-level error: reset the offending input to its last good
        // value and show the server message next to the line.
        this.#handleCartError(line, parsedResponseText);
      } else {
        // Network failure or unexpected error: reset the offending input to
        // server truth so no phantom optimistic quantity lingers, and surface a
        // generic toast since there is no line-level message to display.
        this.#resetLineInput(line);
        const message = parsedResponseText?.description
          ?? parsedResponseText?.message
          ?? window.cartCheckoutTranslations?.errorGeneric
          ?? 'Oops, we ran into a problem. Please try again.';
        window.Alpine?.store('toastStore')?.error(message);
      }
    } catch (reconcileError) {
      // Never let reconciliation throw out of the queue's error path.
      console.error(reconcileError);
    } finally {
      this.#endBurst();
    }
  }

  /**
   * Resets a line's quantity input back to its last server-rendered value,
   * clearing any optimistic value left by an aborted burst. No-op if missing.
   * @param {number} line - The cart line index.
   */
  #resetLineInput(line) {
    const quantitySelector = this.refs.quantitySelectors?.[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = quantityInput.defaultValue;
    }
  }

  /**
   * Ends the current cart-write burst: re-enable the cart and close the perf marker.
   */
  #endBurst() {
    this.#enableCartItems();
    if (this.#burstPerfMarker) {
      cartPerformance.measureFromMarker(this.#burstPerfMarker);
      this.#burstPerfMarker = null;
    }
  }

  updateCartItemCount(newCartItemCount = 0) {
    const cartItemCountElement = document.querySelector('.header-actions__cart-icon [data-cart-item-count]');
    if (cartItemCountElement && cartItemCountElement instanceof HTMLElement) {
      cartItemCountElement.textContent = String(newCartItemCount);
      if (newCartItemCount > 0) {
        cartItemCountElement.classList.remove('hidden');
      } else {
        cartItemCountElement.classList.add('hidden');
      } 
    }
  }
  /**
   * Estimated loyalty points for the cart: Order PV × earning rate.
   *
   * Uses the same total PV the cart already shows (promotions.currentPv when
   * loaded, otherwise the SSR seed on data-current-pv). That total is
   * whole-cart earnable PV (loyalty + one-time, free gifts excluded) — not
   * just subscription lines.
   */
  calculateTotalLoyaltyPoints() {
    const promoStore = window.Alpine?.store?.('promotions');
    // Match Order PV display: prefer API currentPv when > 0, else SSR seed.
    let totalPv = 0;
    if (promoStore?.currentPv > 0) {
      totalPv = promoStore.currentPv;
    } else {
      const pvElement = document.querySelector('[data-free-gifts-milestones][data-current-pv]');
      totalPv = parseFloat(pvElement?.getAttribute('data-current-pv') || '0') || 0;
    }

    totalPv = Math.round(totalPv * 100) / 100;
    const total = this.getEarnedPointsFromQv(totalPv);
    const value = total ? total.toString() : '0';

    // Update ALL elements (desktop and mobile)
    const totalElements = document.querySelectorAll('[data-cart-total-loyalty-points-value]');
    totalElements.forEach((el) => {
      el.textContent = value;
      el.setAttribute('data-cart-total-loyalty-points-value', value);
    });
  }

  getEarnedPointsFromQv = (qv = 0) => {
    const raw = sessionStorage.getItem('yl_rewards_cache') || '{}';
    const cached = JSON.parse(raw);
    const earningPercent = cached?.data?.earningPercentage || 10;
    return Math.round(qv * earningPercent) / 100;
  }
  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = async (event) => {
    if (event instanceof DiscountUpdateEvent) {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, false);
      replaceSection(this.sectionId, html);
      this.calculateTotalLoyaltyPoints();
      return;
    }
    if (event.target === this) return;
    if (event.detail?.data?.source === 'refreshCart') return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      replaceSection(this.sectionId, cartItemsHtml);
    } else {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, false);
      replaceSection(this.sectionId, html);
      this.calculateTotalLoyaltyPoints();
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}

customElements.define('cart-loyalty-points-loading', class extends HTMLElement {
  connectedCallback() {
    /** @type {CartItemsComponent|null} */
    const cartComponent = document.querySelector('cart-items-component');
    cartComponent?.calculateTotalLoyaltyPoints();
  }
});// 1770366752
